import { FormikConfig, useFormik } from 'formik';
import moment from 'moment';
import { stripAiTemplateNamePrefix } from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.functions';
import { Button, Input, Select } from 'react-daisyui';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { durationOptions } from '../../../../../../../pages/Agenda/components/Calendar/Calendar.constants';
import { getAllowedFields } from '../../../../../../../pages/Agenda/components/Calendar/Calendar.functions';
import { initialValues } from '../../../../../../../pages/Agenda/components/Calendar/Calendar.hook';
import {
  tCreateEventForm,
  tOption,
} from '../../../../../../../pages/Agenda/components/Calendar/Calendar.types';
import { CreateEventSchema } from '../../../../../../../pages/Agenda/components/Calendar/forms/CreateEvent/CreateEvent.schema';
import { useUserContext } from '../../../../../../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../../../../../../providers/WorkspaceProvider/Workspace.context';
import { useAgendaActionContext } from '../AgendaAction.context';
import '../AgendaAction.styles.scss';
import CustomSelect from './CustomSelect.component';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { getModuleName } from '../../../../../Chat.utils';

export default function CreateEvent() {
  const { templates, onCreateChatEventSubmit } = useAgendaActionContext();
  const { workspaceInfo } = useWorkspaceContext();
  const { currentUser } = useUserContext();
  const { t } = useTranslation('chat/chat');
  const { trackEvent } = useAnalytics();

  const now = new Date();

  const formikConfig: FormikConfig<tCreateEventForm> = {
    initialValues: {
      ...initialValues,
      startDate: now,
      time: now,
      duration: '00:15:00',
      guests: [
        {
          value: currentUser?.authId || '',
          label: currentUser?.name || '',
        },
      ],
    },
    validationSchema: CreateEventSchema,
    onSubmit: async (values) => {
      await onCreateChatEventSubmit(values);
      trackEvent('front.event.schedule.create.save_btn.click', {
        displayName: 'Crear y guardar evento',
        description:
          'Cuántos eventos nuevos se crearon desde la conversación del lead',
        ctaType: getModuleName(),
      });
    },
  };

  const formik = useFormik(formikConfig);
  let guestsOptions: tOption[] = [];

  if (workspaceInfo && Object.values(workspaceInfo.users).length) {
    guestsOptions = Object.values(workspaceInfo.users).map((user) => ({
      value: user.authId,
      label: user.name,
    }));
  }

  // The option's value stays the id, so stripping only touches what is read.
  // No AI chip here: an <option> renders text, not markup.
  const templateOptions: tOption[] = templates.map((template) => ({
    value: template.id,
    label: stripAiTemplateNamePrefix(template.name),
  }));

  const defaultOptions: tOption[] = [
    {
      value: currentUser?.authId || '',
      label: currentUser?.name || '',
    },
  ];
  const allowedFields = getAllowedFields(templates, formik.values);

  const isDisabledButton = (() => {
    if (!!allowedFields.description && !formik.values.description) return true;
    if (!!allowedFields.address && !formik.values.address) return true;
    if (!!allowedFields.link && !formik.values.link) return true;
    if (!!allowedFields.notes && !formik.values.note) return true;
    if (!formik.values.guests) return true;
    if (!formik.values.templateId) return true;

    return false;
  })();

  return (
    <form onSubmit={formik.handleSubmit} className="w-full mt-2 px-4">
      <div className="grid grid-cols-3 gap-4 gap-y-6 min-w-full">
        <div>
          <Select
            id="templateId"
            name="templateId"
            onChange={formik.handleChange}
            value={formik.values.templateId}
            className="w-full"
          >
            <option value="" className="flex flex-row">
              {t('agendaAction.selectTemplate')}
            </option>
            {templateOptions.map((template) => (
              <option key={template.value} value={template.value}>
                {template.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Input
            id="title"
            name="title"
            type="text"
            placeholder={t('agendaAction.eventTitle')}
            onChange={formik.handleChange}
            value={formik.values.title}
            className="w-full"
          />
          {formik.touched.title && formik.errors.title ? (
            <div className="text-red-500">{formik.errors.title}</div>
          ) : null}
        </div>

        <div>
          <DatePicker
            className="input input-bordered"
            selected={formik.values.startDate}
            popperClassName="ReactDatepicker_Popper"
            wrapperClassName="ReactDatepicker_Wrapper"
            onChange={(val) => formik.setFieldValue('startDate', val)}
            minDate={moment().toDate()}
          />
        </div>

        <div>
          <DatePicker
            className="input input-bordered"
            selected={formik.values.time}
            popperClassName="ReactDatepicker_Popper"
            wrapperClassName="ReactDatepicker_Wrapper w-full"
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            onChange={(val) => formik.setFieldValue('time', val)}
            minDate={new Date()}
          />
        </div>

        {!!allowedFields.description && (
          <div>
            <Input
              id="description"
              name="description"
              placeholder={t('agendaAction.eventDescription')}
              onChange={formik.handleChange}
              value={formik.values.description}
              className="w-full"
            />
            {formik.touched.description && formik.errors.description ? (
              <div className="text-red-500">{formik.errors.description}</div>
            ) : null}
          </div>
        )}

        <div>
          {formik.touched.duration && formik.errors.duration ? (
            <div className="text-red-500">{formik.errors.duration}</div>
          ) : null}
          <Select
            className="w-full"
            id="duration"
            name="duration"
            onChange={formik.handleChange}
            value={formik.values.duration}
          >
            {durationOptions.map((option) => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {!!allowedFields.address && (
          <div>
            <Input
              id="address"
              name="address"
              type="text"
              placeholder="Ej. Monte Alban 304, Int. 5"
              onChange={formik.handleChange}
              value={formik.values.address}
              className="w-full"
            />
            {formik.touched.address && formik.errors.address ? (
              <div className="text-red-500">{formik.errors.address}</div>
            ) : null}
          </div>
        )}

        {!!allowedFields.links && (
          <div>
            <Input
              id="link"
              name="link"
              type="url"
              placeholder="Ej. http://www.ejemplo.com"
              onChange={formik.handleChange}
              value={formik.values.link}
              className="w-full"
            />
            {formik.touched.link && formik.errors.link ? (
              <div className="text-red-500">{formik.errors.link}</div>
            ) : null}
          </div>
        )}

        {!!allowedFields.notes && (
          <div>
            <Input
              id="note"
              name="note"
              type="text"
              placeholder={t('agendaAction.eventNote')}
              onChange={formik.handleChange}
              value={formik.values.note}
              className="w-full"
            />
            {formik.touched.note && formik.errors.note ? (
              <div className="text-red-500">{formik.errors.note}</div>
            ) : null}
          </div>
        )}
      </div>

      {!!allowedFields.users && (
        <div className="w-full mt-4">
          <CustomSelect
            options={guestsOptions}
            defaultOptions={defaultOptions}
            onChange={(value) => formik.setFieldValue('guests', value)}
          />
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          color="primary"
          className="my-2 px-16"
          disabled={isDisabledButton}
        >
          {t('agendaAction.scheduleEvent')}
        </Button>
      </div>
    </form>
  );
}
